import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star, Clock, Shield, IndianRupee, Users, CheckCircle } from 'lucide-react';

const WhyChoose = () => {
  const benefits = [
    {
      icon: MapPin,
      title: 'Doorstep Convenience',
      description: 'No travel, no waiting. We come to you – at your home, office, or apartment.',
    },
    {
      icon: Star,
      title: 'Premium Quality',
      description: 'Best-in-class products and highly trained professionals for flawless results.',
    },
    {
      icon: Clock,
      title: 'Time-Saving',
      description: 'Efficient service at your preferred location. Use your time for what matters most.',
    },
    {
      icon: Shield,
      title: 'Vehicle Value Protection',
      description: 'Regular professional care maintains your car\'s appearance and resale value.',
    },
    {
      icon: Users,
      title: 'Customer-First Approach',
      description: 'Responsive, personalized service tailored to your specific needs.',
    },
    {
      icon: IndianRupee,
      title: 'Transparent Pricing',
      description: 'No hidden charges. What you see is what you pay.',
    },
  ];

  const comparisons = [
    { feature: 'Doorstep Service', washora: true, others: false },
    { feature: 'Premium Products', washora: true, others: 'Sometimes' },
    { feature: 'Trained Professionals', washora: true, others: 'Varies' },
    { feature: 'Transparent Pricing', washora: true, others: false },
    { feature: 'VIP Membership', washora: true, others: false },
    { feature: 'Eco-Friendly Methods', washora: true, others: false },
    { feature: '15-Min Response', washora: true, others: false },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="why-choose-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            The Washora Advantage
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Why Choose Washora
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            We're not just another car wash service. We're your trusted partner in vehicle care, delivering excellence at your doorstep.
          </p>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1E88E5] transition-colors">
                  <benefit.icon className="h-7 w-7 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#64748B]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Washora vs Others
            </h2>
            <p className="text-[#64748B]">See how we compare to traditional car wash services.</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-3 bg-[#0B2C5D] text-white py-4">
              <div className="px-6 font-heading font-semibold">Feature</div>
              <div className="text-center font-heading font-semibold text-[#F7931E]">Washora</div>
              <div className="text-center font-heading font-semibold">Others</div>
            </div>
            {comparisons.map((item, index) => (
              <div 
                key={index}
                className={`grid grid-cols-3 py-4 ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
              >
                <div className="px-6 text-[#0B2C5D] font-medium">{item.feature}</div>
                <div className="text-center">
                  {item.washora === true ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mx-auto" />
                  ) : (
                    <span className="text-[#64748B]">{item.washora}</span>
                  )}
                </div>
                <div className="text-center">
                  {item.others === true ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mx-auto" />
                  ) : item.others === false ? (
                    <span className="text-red-500 text-xl">✗</span>
                  ) : (
                    <span className="text-[#64748B]">{item.others}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-20 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Trusted by Hundreds
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Happy Customers' },
              { value: '1000+', label: 'Cars Washed' },
              { value: '4.9', label: 'Star Rating' },
              { value: '100%', label: 'Satisfaction' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-[#F7931E] mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
            Experience the Washora Difference
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-8">
            Join hundreds of satisfied customers who've made the switch to premium doorstep car care.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30"
            data-testid="why-book-now"
          >
            Book Now <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyChoose;
